import { zip } from "lodash";
import { AmlStatusEnum } from "../model";
import { AmlStatus as S } from "../constant/amlConstant";
import StateMachine, { BaseEvent, StateMachineConfig, StateObject, Transition } from "../utils/stateMachine";
import { StatusNaming } from "../constant/amlConstant";

/**
 * Event type for AML transitions
 */
interface AmlEvent extends BaseEvent {
type: `TO${AmlStatusEnum}`
}

/**
 * Context type for AML state machine
 * (expand later if you have data to track)
 */
interface AmlContext { }

/**
 * Transition of "Ignore" type. Nothing happened when changing REF to REF.
 */
const I = (): null => null;

/**
 * Transition of "Not Allowed" type. This transition should not happen.
 */
const N = (): null => null;

/**
 * Transition of "Allowed" type. Send request to submit change.
 */
const Y = (
  targetStatus: AmlStatusEnum
): Record<string, Transition<AmlContext, AmlEvent, AmlStatusEnum>> => ({
  [`TO${targetStatus}`]: {
    target: targetStatus,
    actions: ["submitStatusChange"]
  }
});

/**
 * Transition of "To REF" type.
 * This transition is not allowed because user should transfer to REF status first.
 */
const T = (
  targetStatus: AmlStatusEnum
): Record<string, Transition<AmlContext, AmlEvent, AmlStatusEnum>> => ({
  [`TO${targetStatus}`]: {
    target: targetStatus,
    metaData: {
      disabled: true,
      tooltip: `You should set status to ${StatusNaming[S.REF]} first.`
    }
  }
});

/**
 * Collect transitions to create a state object.
 */
const toState = (
  currentState: AmlStatusEnum,
  transitions: Array<
    Record<string, Transition<AmlContext, AmlEvent, AmlStatusEnum>> | null
  >
): Record<AmlStatusEnum, StateObject<AmlContext, AmlEvent, AmlStatusEnum>> => {
  const validTransitions = transitions.filter(Boolean) as Record<
    string,
    Transition<AmlContext, AmlEvent, AmlStatusEnum>
  >[];
  return validTransitions.reduce(
    (acc, b) => {
      acc[currentState].on = {
        ...acc[currentState].on,
        ...b
      };
      return acc;
    },
    {
      [currentState]: {
        on: {}
      }
    } as Record<AmlStatusEnum, StateObject<AmlContext, AmlEvent, AmlStatusEnum>>
  );
};

/**
 * Using transition factories to create an array of transitions.
 */
const toTransitions = (
  factories: Array<(status: AmlStatusEnum) => any>
): Array<Record<string, Transition<AmlContext, AmlEvent, AmlStatusEnum>> | null> =>
  zip(STATUS_ORDER, factories).map(([status, factory]) =>
    factory?.(status as AmlStatusEnum)
  );

const STATUS_ORDER: AmlStatusEnum[] = [
  S.UNC,
  S.SUB,
  S.REF,
  S.EVI,
  S.AUT,
  S.ACT,
  S.REJ,
  S.NRQ
];

// State definitions (prettier-ignore to keep table format)
const UNC_STATE = toState(S.UNC, toTransitions([I, N, Y, N, N, N, N, N]));// prettier-ignore
const SUB_STATE = toState(S.SUB, toTransitions([N, I, Y, T, T, T, T, N]));// prettier-ignore
const REF_STATE = toState(S.REF, toTransitions([N, N, I, Y, Y, Y, Y, N]));// prettier-ignore
const EVI_STATE = toState(S.EVI, toTransitions([N, N, Y, I, Y, Y, Y, N]));// prettier-ignore
const AUT_STATE = toState(S.AUT, toTransitions([N, N, Y, Y, I, Y, Y, N]));// prettier-ignore
const ACT_STATE = toState(S.ACT, toTransitions([N, N, Y, Y, Y, I, Y, N]));// prettier-ignore
const REJ_STATE = toState(S.REJ, toTransitions([N, N, Y, Y, Y, Y, I, N]));// prettier-ignore
const NRQ_STATE = toState(S.NRQ, toTransitions([N, N, Y, T, T, T, T, I]));// prettier-ignore

export type AmlStateMachine = StateMachine<AmlContext, AmlEvent, AmlStatusEnum>;
export type AmlStateMachineConfig = StateMachineConfig<AmlContext, AmlEvent, AmlStatusEnum>;
/**
 * Create state machine config using initial status.
 */
export const createAmlStatusMachineConfig = (
  initial: AmlStatusEnum
): AmlStateMachineConfig => ({
  initial,
  context: {},
  actions: {},
  states: {
    ...UNC_STATE,
    ...SUB_STATE,
    ...REF_STATE,
    ...EVI_STATE,
    ...AUT_STATE,
    ...ACT_STATE,
    ...REJ_STATE,
    ...NRQ_STATE
  }
});

/**
 * Create state machine with config.
 * Isolate state machine implementation from usage.
 */
const createAmlStatusMachine = (
  config: StateMachineConfig<AmlContext, AmlEvent, AmlStatusEnum>
) => new StateMachine(config);

export default createAmlStatusMachine;
